/**
 * MagicLinkRegisterPage — public landing for admin-issued vendor/pro invites.
 * Route: /invite/:token
 *
 * UX mirrors the regular /register form (role cards, search, grouped category
 * pickers, priest-title selector, catering-team staff declaration) — minus
 * the KYC-style gates:
 *   • no phone input (phone is locked to the admin-issued token)
 *   • no OTP send/verify cycle (the token IS the auth)
 *   • no Terms / Age checkbox section
 *   • no govt-registration gate (the admin already pre-approved this user)
 *
 * Backend: POST /api/magic-link/:token/register — issues session cookies.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Spinner, CheckCircle, MagnifyingGlass,
  User, Storefront, Buildings, Briefcase, Star,
} from '@phosphor-icons/react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import BrandLogo from '../components/BrandLogo';
import CityLocationInput from '../components/CityLocationInput';
import { useAuth } from '../contexts/AuthContext';
import {
  ROLES, VENDOR_CATEGORIES, PROFESSIONAL_GROUPS, ENTERTAINMENT_GROUP,
  PRIEST_TITLE_GROUPS, SEARCH_INDEX, fmtINR,
} from '../data/registerData';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Resolve `iconKey` strings from registerData into the actual icon components.
const ICONS = { user: User, storefront: Storefront, buildings: Buildings, briefcase: Briefcase, star: Star };

// Magic links are for paid roles only — admins invite vendors/pros, never customers.
const INVITE_ROLES = ROLES.filter((r) => r.key !== 'customer');

export default function MagicLinkRegisterPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { checkAuth } = useAuth();

  // ─── Link resolution ─────────────────────────────────────────────
  const [loading, setLoading] = useState(true);
  const [linkError, setLinkError] = useState('');
  const [phone, setPhone] = useState('');
  const [expiresAt, setExpiresAt] = useState('');

  // ─── Form state ──────────────────────────────────────────────────
  const [roleKey, setRoleKey] = useState('vendor');
  const [search, setSearch] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const selectedRole = useMemo(
    () => INVITE_ROLES.find((r) => r.key === roleKey) || INVITE_ROLES[0],
    [roleKey],
  );

  const [formData, setFormData] = useState({
    name: '',
    business_name: '',
    category: '',
    location: '',
    description: '',
  });

  // Priest single-title taxonomy
  const [priestTitle, setPriestTitle] = useState({ tradition: '', title: '' });
  // Catering team staff declaration (required when category=catering_team)
  const [cateringStaff, setCateringStaff] = useState({
    chef: 1, assistant_chef: 1, server: 1, cleanup: 1,
  });

  // Auto-lock category for roles that have one (venue_provider).
  useEffect(() => {
    setFormData((f) => ({ ...f, category: selectedRole.category || '' }));
  }, [selectedRole]);

  // ─── Link resolution effect ──────────────────────────────────────
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/magic-link/${token}`);
        if (!mounted) return;
        setPhone(data.phone);
        setExpiresAt(data.expires_at || '');
        // Apply admin-supplied prefill into the form so the recipient
        // only confirms instead of re-typing.
        const pre = data.prefill || {};
        if (pre.role && (pre.role === 'vendor' || pre.role === 'professional')) {
          setRoleKey(pre.role);
        }
        setFormData((f) => ({
          ...f,
          name: pre.name || f.name,
          business_name: pre.business_name || f.business_name,
          location: pre.location || f.location,
          category: pre.category || f.category,
        }));
      } catch (err) {
        if (!mounted) return;
        setLinkError(err?.response?.data?.detail || 'Invite link is invalid.');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [token]);

  // ─── Search ──────────────────────────────────────────────────────
  const searchResults = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q || q.length < 2) return [];
    return SEARCH_INDEX.filter((it) => it.keywords.includes(q)).slice(0, 8);
  }, [search]);

  const pickFromSearch = (result) => {
    setRoleKey(result.roleKey);
    setFormData((f) => ({ ...f, category: result.category }));
    setSearch('');
    setSearchOpen(false);
    toast.success(`Selected: ${result.label}`);
  };

  // ─── Submit ──────────────────────────────────────────────────────
  const submit = async (e) => {
    e?.preventDefault?.();
    if (!formData.name.trim()) { toast.error('Please enter your name'); return; }
    if (!formData.category) { toast.error('Please pick a category'); return; }
    if (!formData.location.trim()) { toast.error('Please enter your city'); return; }
    if (formData.category === 'priest' && !(priestTitle.tradition && priestTitle.title)) {
      toast.error('Pick your wedding tradition and title'); return;
    }
    if (formData.category === 'catering_team') {
      const total = Object.values(cateringStaff).reduce((a, b) => a + (b || 0), 0);
      if (total < 4) { toast.error('Minimum 1 staff member per role'); return; }
    }

    setSubmitting(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/magic-link/${token}/register`,
        {
          name: formData.name.trim(),
          role: selectedRole.role, // 'vendor' or 'professional' — backend gate
          category: formData.category,
          business_name: formData.business_name.trim() || undefined,
          description: formData.description.trim() || undefined,
          location: formData.location.trim(),
          // KYC gate excluded per UX brief — admin already pre-approved by issuing the invite.
          business_registered: false,
          priest_title: formData.category === 'priest' ? priestTitle.title : undefined,
          priest_tradition: formData.category === 'priest' ? priestTitle.tradition : undefined,
          catering_staff: formData.category === 'catering_team' ? cateringStaff : undefined,
        },
        { withCredentials: true },
      );
      if (checkAuth) await checkAuth();
      toast.success('Welcome aboard! Redirecting…');
      setTimeout(() => {
        navigate(selectedRole.role === 'vendor' ? '/dashboard/vendor' : '/dashboard/professional', { replace: true });
      }, 600);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not complete signup');
      setSubmitting(false);
    }
  };

  // ─── Category picker (role-dependent) ────────────────────────────
  const renderCategoryPicker = () => {
    if (selectedRole.key === 'venue_provider') {
      return (
        <div className="p-3 bg-brand-accent/5 border border-brand-primary/20 rounded text-sm" data-testid="ml-venue-locked-info">
          <span className="font-bold">Category:</span> Venue Provider (locked) — you&apos;ll add specific venue types (Mandapam, Banquet, Garden, etc.) inside your dashboard after registration.
        </div>
      );
    }
    if (selectedRole.key === 'vendor') {
      return (
        <div className="space-y-2" data-testid="ml-vendor-categories-list">
          <Label className="text-xs tracking-[0.2em] uppercase font-bold text-text-secondary">Category *</Label>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" role="radiogroup">
            {VENDOR_CATEGORIES.map((c) => {
              const active = formData.category === c.value;
              return (
                <button
                  type="button"
                  key={c.value}
                  onClick={() => setFormData({ ...formData, category: c.value })}
                  className={`text-left p-3 border-2 rounded text-sm transition-all ${active ? 'border-brand-primary bg-brand-accent/10 ring-1 ring-brand-primary' : 'border-[#E8E1D5] hover:border-brand-primary/40'}`}
                  data-testid={`ml-vendor-cat-${c.value}`}
                >
                  {c.label}
                </button>
              );
            })}
          </div>
        </div>
      );
    }
    // professional or entertainment_professional — grouped picker
    const groups = selectedRole.key === 'entertainment_professional'
      ? [ENTERTAINMENT_GROUP]
      : PROFESSIONAL_GROUPS;
    return (
      <div className="space-y-3">
        <Label className="text-xs tracking-[0.2em] uppercase font-bold text-text-secondary">
          {selectedRole.key === 'entertainment_professional' ? 'Pick your entertainment type *' : 'Pick your profession *'}
        </Label>
        <div className="space-y-3">
          {groups.map((g) => (
            <div key={g.id} className="border border-[#E8E1D5] rounded">
              <div className="px-3 py-2 bg-sand-100 text-xs tracking-wider uppercase font-bold text-text-secondary" data-testid={`ml-pro-group-${g.id}-header`}>
                {g.label}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 p-2">
                {g.items.map((it) => {
                  const active = formData.category === it.value;
                  return (
                    <button
                      type="button"
                      key={it.value}
                      onClick={() => setFormData({ ...formData, category: it.value })}
                      className={`text-left text-sm p-2 border-2 rounded transition-all ${active ? 'border-brand-primary bg-brand-accent/10 ring-1 ring-brand-primary' : 'border-[#E8E1D5] hover:border-brand-primary/40'}`}
                      data-testid={`ml-pro-cat-${it.value}`}
                    >
                      {it.label}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // ─── Loading / Error gates ───────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF6EE]" data-testid="magic-link-loading">
        <Spinner size={32} className="animate-spin text-[#8B5E34]" />
      </div>
    );
  }
  if (linkError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF6EE] px-4" data-testid="magic-link-error">
        <Card className="max-w-md w-full border-2 border-red-200">
          <CardHeader>
            <BrandLogo className="h-10 mb-3" />
            <CardTitle className="text-red-700">Invite link unavailable</CardTitle>
            <CardDescription>{linkError}</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-text-secondary mb-4">
              Please ask the admin to send you a fresh invite link, or sign up the regular way using your phone number.
            </p>
            <Button onClick={() => navigate('/register')} className="w-full" data-testid="magic-link-go-register-btn">
              Go to Sign Up
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ─── Main form ───────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-sand-50 flex items-start sm:items-center justify-center p-4 sm:p-8" data-testid="magic-link-form">
      <div className="w-full max-w-3xl">
        <div className="text-center mb-8">
          <div className="inline-flex justify-center"><BrandLogo size="xl" linkTo="/" /></div>
          <div className="mt-3 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-green-50 border border-green-200 text-green-800 text-xs font-bold">
            <CheckCircle size={14} weight="fill" />
            Admin-invited signup — no OTP, no govt-registration needed
          </div>
        </div>

        <Card className="bg-white border-[#E8E1D5]">
          <CardHeader>
            <CardTitle className="font-heading text-3xl">Create your account</CardTitle>
            <CardDescription>
              Phone <span className="font-mono font-bold text-text-primary">{phone}</span> is locked to this invite{expiresAt ? ` (expires ${new Date(expiresAt).toLocaleDateString()})` : ''}.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Search bar */}
            <div className="mb-4">
              <Label className="text-xs tracking-[0.2em] uppercase font-bold text-text-secondary">
                Or search &quot;magician&quot;, &quot;venue&quot;, &quot;photographer&quot;…
              </Label>
              <div className="relative mt-1">
                <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary" />
                <Input
                  type="text"
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setSearchOpen(true); }}
                  onFocus={() => setSearchOpen(true)}
                  placeholder="Type to find your category…"
                  className="pl-9 rounded-none border-[#E8E1D5]"
                  data-testid="ml-search-input"
                />
              </div>
              {searchOpen && searchResults.length > 0 && (
                <div className="mt-1 border-2 border-[#E8E1D5] rounded bg-white shadow-sm divide-y divide-[#E8E1D5]" data-testid="ml-search-results">
                  {searchResults.map((r) => (
                    <button
                      type="button"
                      key={`${r.roleKey}-${r.category}`}
                      onClick={() => pickFromSearch(r)}
                      className="w-full text-left px-3 py-2 hover:bg-sand-100 flex items-center justify-between text-sm"
                      data-testid={`ml-search-result-${r.category}`}
                    >
                      <span>{r.label}</span>
                      <span className="text-[10px] uppercase tracking-wider text-text-secondary font-bold">
                        {INVITE_ROLES.find((rr) => rr.key === r.roleKey)?.title || r.roleKey}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Role cards */}
            <div className="mb-6">
              <Label className="text-xs tracking-[0.2em] uppercase font-bold text-text-secondary mb-3 block">
                I am a…
              </Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3" data-testid="ml-role-cards">
                {INVITE_ROLES.map((r) => {
                  const Icon = ICONS[r.iconKey] || User;
                  const active = roleKey === r.key;
                  return (
                    <button
                      type="button"
                      key={r.key}
                      onClick={() => setRoleKey(r.key)}
                      className={`text-left p-3 border-2 rounded-lg transition-all flex flex-col gap-2 min-h-[110px] min-w-0 overflow-hidden ${active ? 'border-brand-primary bg-brand-accent/10 ring-1 ring-brand-primary' : 'border-[#E8E1D5] hover:border-brand-primary/40'}`}
                      data-testid={`ml-role-card-${r.key}`}
                    >
                      <div className="flex items-start gap-1.5 min-w-0">
                        <Icon size={18} weight={active ? 'fill' : 'regular'} className={`flex-shrink-0 mt-0.5 ${active ? 'text-brand-primary' : 'text-text-secondary'}`} />
                        <span className={`text-sm font-bold break-words leading-tight ${active ? 'text-brand-primary' : 'text-text-primary'}`}>{r.title}</span>
                      </div>
                      <span className="text-[11px] text-text-secondary leading-snug break-words">{r.desc}</span>
                      {r.earnings && (
                        <span className={`mt-auto inline-flex items-center gap-1 text-[10px] font-bold leading-tight px-1.5 py-0.5 rounded break-words ${active ? 'bg-brand-primary text-sand-50' : 'bg-green-50 text-green-800 border border-green-200'}`}
                          data-testid={`ml-role-earnings-${r.key}`}>
                          Earn {fmtINR(r.earnings.min)}–{fmtINR(r.earnings.max)}/{r.earnings.unit}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            <form onSubmit={submit} className="space-y-5">
              {renderCategoryPicker()}

              {/* Priest title selector */}
              {formData.category === 'priest' && (
                <div className="border-2 border-brand-primary/40 rounded p-3 bg-brand-accent/5" data-testid="ml-priest-traditions-block">
                  <Label className="text-xs tracking-[0.2em] uppercase font-bold text-brand-primary mb-1 block">
                    Which wedding can you officiate? *
                  </Label>
                  <p className="text-xs text-text-secondary mb-3">
                    Pick the wedding tradition you perform and your title. Your title will be shown before your name in customer listings.
                  </p>
                  <div className="space-y-3">
                    {PRIEST_TITLE_GROUPS.map((g) => (
                      <div key={g.tradition} className="border-2 border-[#E8E1D5] rounded p-3 bg-white" data-testid={`ml-priest-group-${g.tradition}`}>
                        <div className="text-sm font-bold text-text-primary mb-1">{g.label}</div>
                        <div className="text-[10px] uppercase tracking-wider text-text-secondary mb-2">{g.sub}</div>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                          {g.titles.map((t) => {
                            const checked = priestTitle.tradition === g.tradition && priestTitle.title === t;
                            return (
                              <label
                                key={t}
                                className={`flex items-center gap-2 p-2 border-2 rounded cursor-pointer text-sm transition-all ${checked ? 'border-brand-primary bg-brand-accent/10' : 'border-[#E8E1D5] hover:border-brand-primary/40'}`}
                                data-testid={`ml-priest-title-${g.tradition}-${t.toLowerCase()}`}
                              >
                                <input
                                  type="radio"
                                  name="ml_priest_title"
                                  checked={checked}
                                  onChange={() => setPriestTitle({ tradition: g.tradition, title: t })}
                                  className="h-4 w-4 accent-brand-primary flex-shrink-0"
                                />
                                <span>{t}</span>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                  {priestTitle.tradition && priestTitle.title && (
                    <p className="text-xs text-green-700 mt-2 font-medium">
                      Selected: <strong>{priestTitle.title}</strong> ({priestTitle.tradition})
                    </p>
                  )}
                </div>
              )}

              {/* Catering team staff declaration */}
              {formData.category === 'catering_team' && (
                <div className="border-2 border-brand-primary/40 rounded p-3 bg-brand-accent/5" data-testid="ml-catering-staff-block">
                  <Label className="text-xs tracking-[0.2em] uppercase font-bold text-brand-primary mb-1 block">
                    Declare your team size *
                  </Label>
                  <p className="text-xs text-text-secondary mb-3">
                    Customers will see your team strength when booking. Minimum 1 per role is required.
                  </p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {[
                      { key: 'chef', label: 'Chefs' },
                      { key: 'assistant_chef', label: 'Asst Chefs' },
                      { key: 'server', label: 'Servers' },
                      { key: 'cleanup', label: 'Cleanup' },
                    ].map(({ key, label }) => (
                      <div key={key} className="space-y-1">
                        <Label className="text-xs font-bold text-text-secondary">{label} *</Label>
                        <Input
                          type="number"
                          min="1"
                          value={cateringStaff[key]}
                          onChange={(e) => {
                            const v = Math.max(0, parseInt(e.target.value || '0', 10));
                            setCateringStaff((s) => ({ ...s, [key]: v }));
                          }}
                          className={`rounded-none h-9 ${cateringStaff[key] < 1 ? 'border-red-400 bg-red-50/30' : 'border-[#E8E1D5]'}`}
                          data-testid={`ml-catering-staff-${key}`}
                        />
                      </div>
                    ))}
                  </div>
                  <p className="text-[10px] text-text-secondary mt-2 italic">
                    Total team size: {Object.values(cateringStaff).reduce((a, b) => a + (b || 0), 0)}
                  </p>
                </div>
              )}

              {/* Full name */}
              <div>
                <Label htmlFor="ml-name" className="text-xs tracking-[0.2em] uppercase font-bold text-text-secondary">Full Name *</Label>
                <Input
                  id="ml-name"
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="rounded-none border-[#E8E1D5] focus:ring-brand-primary"
                  data-testid="ml-name-input"
                />
              </div>

              {/* Business name — vendors only */}
              {selectedRole.role === 'vendor' && (
                <div>
                  <Label htmlFor="ml-business-name" className="text-xs tracking-[0.2em] uppercase font-bold text-text-secondary">
                    {selectedRole.key === 'venue_provider' ? 'Venue / Business Name' : 'Business Name'}
                  </Label>
                  <Input
                    id="ml-business-name"
                    type="text"
                    value={formData.business_name}
                    onChange={(e) => setFormData({ ...formData, business_name: e.target.value })}
                    className="rounded-none border-[#E8E1D5] focus:ring-brand-primary"
                    data-testid="ml-business-name-input"
                  />
                </div>
              )}

              {/* City */}
              <CityLocationInput
                value={formData.location}
                onChange={(v) => setFormData({ ...formData, location: v })}
                label="City / Current Location"
                required
                testId="ml-location"
              />

              {/* Description / About */}
              <div>
                <Label htmlFor="ml-description" className="text-xs tracking-[0.2em] uppercase font-bold text-text-secondary">
                  {selectedRole.role === 'professional' ? 'About You' : 'Description'}
                </Label>
                <Textarea
                  id="ml-description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  placeholder={selectedRole.role === 'professional' ? 'Describe your experience and services...' : ''}
                  className="rounded-none border-[#E8E1D5] focus:ring-brand-primary"
                  data-testid="ml-description-input"
                />
              </div>

              <Button
                type="submit"
                disabled={submitting}
                className="w-full bg-brand-primary hover:bg-[#A84A38] text-sand-50 rounded-none disabled:opacity-50"
                data-testid="ml-submit-btn"
              >
                {submitting ? 'Creating account…' : 'Create my account'}
              </Button>
              <p className="text-[11px] text-text-secondary text-center">
                By continuing you accept BeeWeddy&apos;s Terms of Use and Privacy Policy.
              </p>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
